import React, { useState } from 'react';
import {
    Card,
    CardBody,
    CardHeader,
    CardTitle,
    Row,
    Col,
    Alert,
    Badge,
    Button,
    Collapse
} from 'reactstrap';

const ApplicationInstructions = () => {
    const [isStepsExpanded, setIsStepsExpanded] = useState(false); // Start collapsed
    const [isPdfTipsExpanded, setIsPdfTipsExpanded] = useState(false);

    const toggleSteps = () => setIsStepsExpanded(!isStepsExpanded);
    const togglePdfTips = () => setIsPdfTipsExpanded(!isPdfTipsExpanded);

    const steps = [
        {
            step: 1,
            title: "Complete Your Application Online",
            description: "Fill out all sections of the digital application form with accurate information.",
            icon: "fas fa-edit",
            color: "primary",
            details: [
                "Fill out personal details completely",
                "Upload all required documents digitally",
                "Review each section before proceeding",
                "Submit the application online"
            ]
        },
        {
            step: 2,
            title: "Download PDF Application",
            description: "After submission, download the PDF version of your completed application.",
            icon: "fas fa-download",
            color: "info",
            details: [
                "Go to your Applications list",
                "Click the download button (📥) next to your submitted application",
                "Save the PDF file to your computer",
                "Verify all information is correct in the PDF"
            ]
        },
        {
            step: 3,
            title: "Print the Application",
            description: "Print the downloaded PDF application on clean, white paper.",
            icon: "fas fa-print",
            color: "warning",
            details: [
                "Use good quality white paper (A4 size recommended)",
                "Print in high resolution for clear text",
                "Ensure all pages are printed clearly",
                "Do not print double-sided if signatures are required on both sides"
            ]
        },
        {
            step: 4,
            title: "Sign Each Page",
            description: "Add your signature and get signatures from community references on each page.",
            icon: "fas fa-pen-nib",
            color: "success",
            details: [
                "Sign your name on each page where indicated",
                "Use blue or black ink pen only",
                "Ensure signatures are clear and legible",
                "Get signatures from required community reference persons",
                "Each reference person should sign on designated areas",
                "Add date next to each signature"
            ]
        },
        {
            step: 5,
            title: "Scan the Signed Document",
            description: "Create a digital copy of your signed application by scanning or photographing it.",
            icon: "fas fa-camera",
            color: "secondary",
            details: [
                "Use a scanner for best quality (recommended)",
                "Alternatively, use a clear camera/phone camera",
                "Ensure all pages are captured clearly",
                "Check that all signatures are visible",
                "Scan in color if possible",
                "Save as high-resolution images"
            ]
        },
        {
            step: 6,
            title: "Convert to PDF",
            description: "Combine all scanned pages into a single PDF file under 10MB.",
            icon: "fas fa-file-pdf",
            color: "danger",
            details: [
                "Combine all scanned pages into one PDF file",
                "Ensure pages are in correct order",
                "Check that file size is under 10MB",
                "If file is too large, compress it (see tips below)",
                "Name the file clearly (e.g., 'YourName_SignedApplication.pdf')"
            ]
        },
        {
            step: 7,
            title: "Upload Signed Application",
            description: "Upload the final PDF through the upload button in your application list.",
            icon: "fas fa-upload",
            color: "success",
            details: [
                "Go to your Applications list",
                "Click the upload button (📤) next to your application",
                "Select your signed PDF file",
                "Wait for upload confirmation",
                "Verify the upload was successful"
            ]
        }
    ];

    const pdfTips = [
        {
            title: "PDF Compression Tools",
            icon: "fas fa-file-archive",
            tips: [
                "Use online tools like SmallPDF, iLovePDF, or PDF24",
                "Adobe Acrobat (if available) - File > Optimize PDF",
                "Preview on Mac - Export > Reduce File Size",
                "Google Drive - Right-click PDF > Open with > Google Docs, then Download as PDF"
            ]
        },
        {
            title: "Scanner Settings",
            icon: "fas fa-sliders-h",
            tips: [
                "Resolution: 300 DPI (recommended for documents)",
                "Color: Color or Grayscale (avoid Black & White for signatures)",
                "Format: PDF (preferred) or high-quality JPEG",
                "Compression: Medium to High (to reduce file size)"
            ]
        },
        {
            title: "Mobile Scanning Apps",
            icon: "fas fa-camera-retro",
            tips: [
                "CamScanner (Android/iOS)",
                "Adobe Scan (Free, Android/iOS)",
                "Microsoft Office Lens (Android/iOS)",
                "Built-in camera apps often have document scan mode"
            ]
        },
        {
            title: "File Size Management",
            icon: "fas fa-balance-scale",
            tips: [
                "Maximum allowed size: 10MB",
                "Typical good size: 2-5MB for multi-page documents",
                "If over 10MB: compress the PDF or reduce scan resolution",
                "Balance quality vs. file size - signatures must be clearly visible"
            ]
        }
    ];

    return (
        <Row className="mt-5">
            <Col lg={12}>
                <Card className="border-0 shadow-sm bg-light">
                    <CardHeader className="bg-gradient text-white" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
                        <div className="d-flex align-items-center justify-content-between">
                            <CardTitle className="mb-0 text-white d-flex align-items-center">
                                <i className="fas fa-graduation-cap me-2"></i>
                                Application Submission Process
                            </CardTitle>
                            <small className="text-white-50">Need help? Read the guide below</small>
                        </div>
                    </CardHeader>
                    <CardBody className="p-4">
                        <Alert color="info" className="mb-4 border-0 shadow-sm">
                            <div className="d-flex align-items-start">
                                <div className="flex-shrink-0">
                                    <div className="avatar-sm bg-info rounded-circle d-flex align-items-center justify-content-center">
                                        <i className="fas fa-file-alt text-white"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-info mb-2">📋 Application Process Overview</h6>
                                    <p className="mb-0 text-muted">
                                        Submit your application online → Download PDF → Print & Sign → Scan & Upload signed copy
                                    </p>
                                </div>
                            </div>
                        </Alert>

                        {/* Step-by-Step Instructions */}
                        <div className="mb-4">
                            <Button
                                color="info"
                                outline
                                onClick={toggleSteps}
                                className="mb-3 d-flex align-items-center w-100 justify-content-between border-0 bg-light text-dark shadow-sm"
                                style={{ padding: '12px 20px' }}
                            >
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-list-ol me-2 text-info"></i>
                                    <strong>Step-by-Step Instructions</strong>
                                </div>
                                <i className={`fas ${isStepsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-info`}></i>
                            </Button>
                            
                            <Collapse isOpen={isStepsExpanded}>
                                <div className="bg-white rounded shadow-sm p-3 mb-4">
                                    <Row>
                                        {steps.map((stepItem, index) => (
                                            <Col lg={4} xl={4} className="mb-2" key={index}>
                                                <Card 
                                                    className="h-100 border-0 shadow-sm" 
                                                    style={{ 
                                                        transition: 'transform 0.2s',
                                                        cursor: 'default'
                                                    }}
                                                    onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-5px)'}
                                                    onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0px)'}
                                                >
                                                    <CardBody className="p-3">
                                                        <div className="d-flex align-items-start">
                                                            <div 
                                                                className={`bg-${stepItem.color} text-white rounded-circle d-flex align-items-center justify-content-center me-3 shadow-sm`} 
                                                                style={{width: '35px', height: '35px', flexShrink: 0, fontSize: '12px'}}
                                                            >
                                                                <i className={stepItem.icon}></i>
                                                            </div>
                                                            <div className="flex-grow-1">
                                                                <div className="d-flex align-items-center mb-2">
                                                                    <Badge color={stepItem.color} className="me-2 px-2 py-1" style={{fontSize: '10px'}}>
                                                                        {stepItem.step}
                                                                    </Badge>
                                                                    <h6 className="mb-0 text-dark" style={{fontSize: '14px'}}>{stepItem.title}</h6>
                                                                </div>
                                                                <p className="text-muted mb-2" style={{fontSize: '12px', lineHeight: '1.3'}}>
                                                                    {stepItem.description}
                                                                </p>
                                                                <div className="small">
                                                                    {stepItem.details.map((detail, idx) => (
                                                                        <div key={idx} className="d-flex align-items-start mb-1">
                                                                            <i className="fas fa-arrow-right text-success me-2 mt-1" style={{fontSize: '8px'}}></i>
                                                                            <span className="text-muted" style={{fontSize: '11px', lineHeight: '1.4'}}>{detail}</span>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </Collapse>
                        </div>

                        {/* PDF Tips and Tools */}
                        <div>
                            <Button
                                color="info"
                                outline
                                onClick={togglePdfTips}
                                className="mb-3 d-flex align-items-center w-100 justify-content-between border-0 bg-light text-dark shadow-sm"
                                style={{ padding: '12px 20px' }}
                            >
                                <div className="d-flex align-items-center">
                                    <i className="fas fa-file-pdf me-2 text-danger"></i>
                                    <strong>PDF Tools & Compression Tips</strong>
                                </div>
                                <i className={`fas ${isPdfTipsExpanded ? 'fa-chevron-up' : 'fa-chevron-down'} text-info`}></i>
                            </Button>
                            
                            <Collapse isOpen={isPdfTipsExpanded}>
                                <div className="bg-white rounded shadow-sm p-3 mb-4">
                                    <Row>
                                        {pdfTips.map((tipSection, index) => (
                                            <Col md={6} lg={3} className="mb-3" key={index}>
                                                <Card className="h-100 border-0 shadow-sm" style={{backgroundColor: '#f8f9fa'}}>
                                                    <CardBody className="p-3">
                                                        <div className="d-flex align-items-center mb-3">
                                                            <div className="avatar-sm bg-info rounded-circle d-flex align-items-center justify-content-center me-2">
                                                                <i className={`${tipSection.icon} text-white`}></i>
                                                            </div>
                                                            <h6 className="mb-0 text-dark">{tipSection.title}</h6>
                                                        </div>
                                                        <div className="small">
                                                            {tipSection.tips.map((tip, idx) => (
                                                                <div key={idx} className="d-flex align-items-start mb-2">
                                                                    <i className="fas fa-chevron-right text-info me-2 mt-1" style={{fontSize: '10px'}}></i>
                                                                    <span className="text-muted">{tip}</span>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </CardBody>
                                                </Card>
                                            </Col>
                                        ))}
                                    </Row>
                                </div>
                            </Collapse>
                        </div>

                        {/* Final Reminder */}
                        <Alert color="success" className="mt-4 border-0 shadow-sm">
                            <div className="d-flex align-items-start">
                                <div className="flex-shrink-0">
                                    <div className="avatar-sm bg-success rounded-circle d-flex align-items-center justify-content-center">
                                        <i className="fas fa-tasks text-white"></i>
                                    </div>
                                </div>
                                <div className="flex-grow-1 ms-3">
                                    <h6 className="text-success mb-2">📋 Final Submission Checklist</h6>
                                    <Row className="small">
                                        <Col md={6}>
                                            <div className="d-flex mb-1">
                                                <i className="fas fa-check-circle text-success me-2 mt-1" style={{fontSize: '12px'}}></i>
                                                <span>PDF file must be under 10MB</span>
                                            </div>
                                            <div className="d-flex mb-1">
                                                <i className="fas fa-check-circle text-success me-2 mt-1" style={{fontSize: '12px'}}></i>
                                                <span>All signatures must be clearly visible</span>
                                            </div>
                                            <div className="d-flex mb-1">
                                                <i className="fas fa-check-circle text-success me-2 mt-1" style={{fontSize: '12px'}}></i>
                                                <span>Use blue or black ink for signatures</span>
                                            </div>
                                        </Col>
                                        <Col md={6}>
                                            <div className="d-flex mb-1">
                                                <i className="fas fa-check-circle text-success me-2 mt-1" style={{fontSize: '12px'}}></i>
                                                <span>Community reference signatures are mandatory</span>
                                            </div>
                                            <div className="d-flex mb-1">
                                                <i className="fas fa-check-circle text-success me-2 mt-1" style={{fontSize: '12px'}}></i>
                                                <span>Upload complete signed document to finalize</span>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </Alert>
                    </CardBody>
                </Card>
            </Col>
        </Row>
    );
};

export default ApplicationInstructions;
